// Shared by TemplateManageScreen's and ChannelManageScreen's CSV import
// sheets (signals and messages respectively) - both let someone override
// the backend's auto-detected delimiter/quoting once it's guessed wrong
// for their file, so this one small module is the single source of truth
// for the option list and the CSVFormatOptions conversion, rather than
// each screen inventing its own copy of "auto"/"comma"/"tab"/etc.
export type DelimiterChoice = 'auto' | 'comma' | 'tab' | 'space' | 'semicolon' | 'pipe' | 'custom';

export const DELIMITER_OPTIONS: { value: DelimiterChoice; label: string }[] = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'comma', label: 'Comma (,)' },
  { value: 'tab', label: 'Tab' },
  { value: 'space', label: 'Space' },
  { value: 'semicolon', label: 'Semicolon (;)' },
  { value: 'pipe', label: 'Pipe (|)' },
  { value: 'custom', label: 'Custom…' },
];

// Turns the UI's delimiter choice into the string the backend's
// resolveDelimiter expects - see backend/modules/template/transitions/
// template.go's identical name/rune mapping.
export function resolveDelimiterParam(choice: DelimiterChoice, custom: string): string {
  return choice === 'custom' ? custom || 'auto' : choice;
}

// ---- CSV writing (export) ----------------------------------------------
//
// The counterpart to backend/modules/template/transitions/template.go's
// readCSVRows (parsing) - building well-formed CSV is simple enough
// (unlike parsing an arbitrary uploaded file, there's no unknown format
// to guess at) that it doesn't need a round trip to the backend the way
// import does; this runs entirely client-side, comma-delimited, RFC 4180
// quoting so the result re-imports cleanly through the same CSV-import
// flow this app already has.

// Quotes a field only if it actually needs it (contains a comma, quote,
// or newline) - matches how a real spreadsheet app exports, rather than
// quoting every field unconditionally.
function csvField(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function rowsToCSV(rows: string[][]): string {
  return rows?.map((row) => row.map(csvField).join(',')).join('\r\n');
}

// Triggers a browser download of `content` as `filename` - no server
// round trip, no temp DOM state left behind (the object URL and the
// anchor are both cleaned up immediately after the click).
export function downloadCSV(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// The "downloadable error report for bad rows" CsvMessageImportSheet.tsx's
// callers offer whenever a message import (dataApi's importMessagesCSV)
// comes back with skippedRows non-empty - reuses rowsToCSV/downloadCSV
// above rather than inventing a second CSV-writing path.
export function downloadSkippedRowsReport(rows: { row: number; reason: string }[]): void {
  const table = [['Row', 'Reason'], ...rows.map((r) => [String(r.row), r.reason])];
  downloadCSV('import-errors.csv', rowsToCSV(table));
}
